import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { FronteggProvider, FronteggThemeOptions } from '@frontegg/react';
import { useAppAuth } from './hooks/useAppAuth';
import { ContextHolder } from '@frontegg/rest-api';
import { AuthenticationApi } from '@frontegg/rest-api/auth';
import AuthService from './services/auth.service';
import { PliableAuthProvider } from './contexts/PliableAuthContext';

const authService = AuthService.getInstance();

// ─── Frontegg-specific setup (skipped entirely for OIDC tenants) ─────────────

if (!authService.oidcEnabled) {
  // Monkey-patch Frontegg's token refresh on custom domains with a handoff token.
  // Safari ITP blocks the cookie-based refresh call to auth.app.pliable.co.
  if (authService.handoffToken && authService.isCustomDomain) {
    const handoffJwt = authService.handoffToken;

    function decodeJwtPayload(token: string): any {
      try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
      } catch { return {}; }
    }

    AuthenticationApi.prototype.refreshTokenV3 = async function () {
      const token = sessionStorage.getItem('plb_handoff_token') || handoffJwt;
      const claims = decodeJwtPayload(token);

      if (claims.exp && claims.exp * 1000 < Date.now()) {
        throw new Error('Handoff token expired');
      }

      ContextHolder.setAccessToken(token);

      const activeTenant = {
        tenantId: claims.tenantId || '',
        name: claims.tenantId || '',
      };

      return {
        user: {
          sub: claims.sub || '',
          id: claims.sub || '',
          email: claims.email || '',
          name: claims.name || '',
          tenantId: claims.tenantId || '',
          tenantIds: claims.tenantIds || [claims.tenantId],
          roles: claims.roles || [],
          permissions: claims.permissions || [],
          accessToken: token,
          verified: true,
          mfaEnrolled: false,
          activatedForTenant: true,
          tenants: [activeTenant],
          metadata: '',
        },
        tenants: [activeTenant],
        activeTenant,
      } as any;
    };
  }

  // Module-level guard for SSO bounce redirect
  let ssoBounceFired = false;

  function ssoRedirectInterceptor(path: string, opts?: any) {
    if (authService.handoffToken && authService.isCustomDomain) {
      return;
    }

    if (path.includes('/oauth/authorize')) {
      if (authService.isCustomDomain && authService.ssoLoginUrl && !ssoBounceFired && !sessionStorage.getItem('plb_sso_attempted') && !authService.handoffToken) {
        ssoBounceFired = true;
        sessionStorage.setItem('plb_sso_attempted', Date.now().toString());
        const returnTo = encodeURIComponent(window.location.origin);
        const returnToken = encodeURIComponent(authService.returnToken || '');
        const idpUrl = encodeURIComponent(authService.ssoLoginUrl!);
        window.location.href = `${authService.authAppUrl}/api/v1/env/sso-bounce?idpUrl=${idpUrl}&returnTo=${returnTo}&returnToken=${returnToken}`;
      } else if (authService.isCustomDomain && !authService.handoffToken) {
        window.location.href = '/ext-login';
      } else if (!authService.isCustomDomain) {
        window.location.href = '/login';
      }
      return;
    }
    if (opts?.refresh) {
      window.location.href = path;
    } else if (opts?.replace) {
      window.history.replaceState(null, '', path);
    } else {
      window.history.pushState(null, '', path);
    }
  }

  ContextHolder.setOnRedirectTo(ssoRedirectInterceptor);

  // Re-set interceptor after FronteggProvider overwrites it (in AppContent)
  var _ssoRedirectInterceptor = ssoRedirectInterceptor;
}

const contextOptions = {
  baseUrl: authService.authUrl,
  clientId: authService.feClientId,
  appId: authService.feAppId,
};

const authOptions = {
  keepSessionAlive: true,
  routes: {
    loginUrl: (authService.useExternalLogin || authService.isCustomDomain) ? '/ext-login' : '/login',
    logoutUrl: '/do/logout',
    authenticatedUrl: '/',
  },
};

const themeOptions: FronteggThemeOptions = {
  loginBox: {},
};

// ─── App Content (shared between both providers) ─────────────────────────────

function AppContent() {
  const { isLoading, isAuthenticated } = useAppAuth();
  const interceptSet = useRef(false);

  // Re-set SSO interceptor for Frontegg mode
  useEffect(() => {
    if (!interceptSet.current && !authService.oidcEnabled && typeof _ssoRedirectInterceptor !== 'undefined') {
      ContextHolder.setOnRedirectTo(_ssoRedirectInterceptor);
      interceptSet.current = true;
    }
  }, []);

  if (authService.useExternalLogin && isAuthenticated) {
    window.location.href = authService.authAppUrl;
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return <Outlet />;
}

// ─── App (switches provider based on OIDC mode) ─────────────────────────────

function App() {
  if (authService.oidcEnabled) {
    // Pliable auth: lightweight provider, no Frontegg SDK, no background refresh
    return (
      <PliableAuthProvider>
        <AppContent />
      </PliableAuthProvider>
    );
  }

  // Frontegg tenants: full Frontegg SDK
  return (
    <FronteggProvider
      authOptions={authOptions}
      contextOptions={contextOptions}
      themeOptions={themeOptions}
      hostedLoginBox={false}
      entitlementsOptions={{ enabled: true }}
    >
      <AppContent />
    </FronteggProvider>
  );
}

export default App;
